import {
  AlertTriangle,
  Check,
  CircleDot,
  FilePenLine,
  Flag,
  Loader2,
  Play,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ProcedurePresentation } from '@/frontend/features/AgentChats/events/agentEvents'
import { useProgressiveProcedureReveal } from './useProgressiveProcedureReveal'

interface Props {
  procedure: ProcedurePresentation | null
  description: string
  running: boolean
  error?: string | null
}

export function ProcedureDraftCanvas({ procedure, description, running, error }: Props) {
  const reveal = useProgressiveProcedureReveal(procedure)
  const displayedProcedure = reveal.procedure
  const fields = displayedProcedure?.draft?.fields
  const steps = fields?.steps || []
  const awaitingReview = displayedProcedure?.progress?.phase === 'awaiting_confirmation'
  // A response can fail after sz-procedures has already committed the draft.
  // In that case the persisted draft is still valid and must remain the canvas
  // source of truth instead of being replaced by a generic transport error.
  const failed = isFailureStatus(displayedProcedure?.status) || (Boolean(error) && !displayedProcedure?.draft)
  const title = displayedProcedure?.title || fields?.name || 'Novo processo'

  return (
    <section
      aria-label="Esboço do processo"
      data-testid="business-process-live-preview"
      data-reveal-state={reveal.revealing ? 'revealing' : 'settled'}
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background"
    >
      <CanvasHeader
        title={title}
        hasDraft={Boolean(displayedProcedure?.draft)}
        running={running}
        revealing={reveal.revealing}
        titleActive={reveal.activeField === 'title'}
        failed={failed}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 [scrollbar-color:var(--border)_transparent] [scrollbar-width:thin] md:px-8 md:py-8">
        <div className="mx-auto w-full max-w-4xl">
          {displayedProcedure?.draft && (steps.length > 0 || reveal.revealing) ? (
            <DraftDiagram
              procedure={displayedProcedure}
              description={description}
              running={running}
              revealing={reveal.revealing}
              activeField={reveal.activeField}
              activeStepPosition={reveal.activeStepPosition}
              steps={steps}
            />
          ) : (
            <PreparingDiagram
              description={description}
              running={running}
              failed={failed}
              progressMessage={displayedProcedure?.progress?.message}
            />
          )}
        </div>
      </div>

      <CanvasFooter
        procedure={displayedProcedure}
        failed={failed}
        awaitingReview={awaitingReview}
        running={running}
        revealing={reveal.revealing}
      />
    </section>
  )
}

function CanvasHeader({ title, hasDraft, running, revealing, titleActive, failed }: {
  title: string
  hasDraft: boolean
  running: boolean
  revealing: boolean
  titleActive: boolean
  failed: boolean
}) {
  return (
    <header className="flex h-[68px] min-h-[68px] shrink-0 items-center gap-3 border-b border-border/70 px-5 py-2 md:px-7">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-agent-soft text-agent">
        <FilePenLine aria-hidden="true" className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className={cn(
            'truncate rounded-sm text-sm font-semibold text-foreground transition-[background-color,color] duration-200',
            titleActive && 'bg-agent-soft px-1 text-agent',
          )}>{title}</h1>
          <span className="shrink-0 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground">
            {hasDraft ? 'Rascunho não aplicado' : 'Esboço'}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          O diagrama acompanha o rascunho salvo nesta conversa.
        </p>
      </div>
      <CanvasStatus running={running} revealing={revealing} failed={failed} hasDraft={hasDraft} />
    </header>
  )
}

function PreparingDiagram({ description, running, failed, progressMessage }: {
  description: string
  running: boolean
  failed: boolean
  progressMessage?: string
}) {
  return (
    <div className="mx-auto max-w-2xl" aria-live="polite">
      <div className="rounded-xl border border-border/70 bg-card px-5 py-4 shadow-xs">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wide text-success">
          <Check aria-hidden="true" className="size-3" />
          Pedido recebido
        </span>
        <h2 className="mt-1.5 text-sm font-medium text-foreground">Descrição inicial</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {description || 'Descreva o resultado e as etapas esperadas na conversa.'}
        </p>
      </div>

      <div aria-hidden="true" className="mx-auto h-8 w-px bg-border" />

      <div className={cn(
        'rounded-xl border px-5 py-4 transition-colors duration-300',
        failed ? 'border-destructive/30 bg-destructive/5' : 'border-agent/25 bg-agent-soft/35',
      )}>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-background text-agent ring-1 ring-border/70">
            {failed
              ? <AlertTriangle aria-hidden="true" className="size-3.5 text-destructive" />
              : running
                ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                : <CircleDot aria-hidden="true" className="size-3.5" />}
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-medium text-foreground">
              {failed ? 'Não foi possível estruturar o processo' : running ? 'Estruturando o processo' : 'Rascunho ainda não salvo'}
            </h2>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {failed
                ? 'O pedido inicial foi preservado. Você pode tentar novamente pela conversa.'
                : running
                  ? progressMessage || 'O agente está definindo o nome, o objetivo e as etapas reais do processo.'
                  : 'O agente concluiu o turno sem salvar uma estrutura de processo. Peça para preparar ou atualizar o rascunho.'}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-[11px] text-faint-foreground">
        O fluxo só aparece quando o rascunho estruturado é salvo — nenhum passo é inferido do texto.
      </p>
    </div>
  )
}

function DraftDiagram({ procedure, description, running, revealing, activeField, activeStepPosition, steps }: {
  procedure: ProcedurePresentation | null
  description: string
  running: boolean
  revealing: boolean
  activeField: 'title' | 'objective' | 'steps' | null
  activeStepPosition: number | null
  steps: NonNullable<ProcedurePresentation['draft']>['fields']['steps']
}) {
  const fields = procedure?.draft?.fields
  if (!steps) return null

  return (
    <div className="animate-message-in">
      <div className={cn(
        'rounded-xl border bg-card px-5 py-4 shadow-xs transition-[border-color,box-shadow] duration-200',
        activeField === 'objective'
          ? 'border-agent/45 shadow-[0_0_0_3px_var(--agent-soft)]'
          : 'border-border/70',
      )}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10.5px] font-medium uppercase tracking-wide text-faint-foreground">Resultado desejado</p>
          {procedure?.draft?.revision && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              Revisão {procedure.draft.revision}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-sm leading-6 text-foreground">{fields?.objective || description}</p>
      </div>

      {(running || revealing) && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-agent-soft/45 px-3 py-2 text-xs text-muted-foreground" aria-live="polite">
          <Loader2 aria-hidden="true" className="size-3.5 animate-spin text-agent" />
          <span>{running
            ? procedure?.progress?.message || 'Atualizando o rascunho do processo…'
            : 'Atualizando o diagrama…'}</span>
        </div>
      )}

      <div className="mb-3 mt-7 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fluxo do processo</h2>
        <span className="text-[11px] text-faint-foreground">{steps.length} {steps.length === 1 ? 'etapa' : 'etapas'}</span>
      </div>
      <ProcessSteps steps={steps} activeStepPosition={activeStepPosition} />
    </div>
  )
}

function ProcessSteps({ steps, activeStepPosition }: {
  steps: NonNullable<ProcedurePresentation['draft']>['fields']['steps']
  activeStepPosition: number | null
}) {
  if (!steps) return null

  return (
    <ol className="flex flex-col">
      {steps.map((step, index) => (
        <li key={`${step.position}-${step.title}`} className="flex animate-message-in flex-col">
          <div className={cn(
            'group flex gap-3 rounded-xl border bg-card px-4 py-3.5 shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:border-agent/35 hover:shadow-sm',
            activeStepPosition === step.position
              ? 'translate-x-1 border-agent/55 shadow-[0_0_0_3px_var(--agent-soft)]'
              : 'border-border',
          )}>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground transition-colors group-hover:bg-agent-soft group-hover:text-agent">
              {step.position}
            </span>
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-agent">
                {stepIcon(index, steps.length)}
                Etapa {step.position}
              </span>
              <h3 className="mt-0.5 text-sm font-medium text-foreground">{step.title}</h3>
              {step.detail && <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.detail}</p>}
            </div>
          </div>
          {index < steps.length - 1 && <div aria-hidden="true" className="ml-[29px] h-5 w-px bg-border" />}
        </li>
      ))}
    </ol>
  )
}

function CanvasFooter({ procedure, failed, awaitingReview, running, revealing }: {
  procedure: ProcedurePresentation | null
  failed: boolean
  awaitingReview: boolean
  running: boolean
  revealing: boolean
}) {
  return (
    <footer className={cn(
      'flex min-h-11 items-center gap-2 border-t border-border/70 px-5 py-2.5 text-xs md:px-7',
      failed ? 'text-destructive' : awaitingReview ? 'text-foreground' : 'text-muted-foreground',
    )}>
      {footerIcon(failed, awaitingReview, running || revealing)}
      {revealing ? 'Atualizando o diagrama' : footerText(procedure, failed, awaitingReview, running)}
    </footer>
  )
}

function CanvasStatus({ running, revealing, failed, hasDraft }: {
  running: boolean
  revealing: boolean
  failed: boolean
  hasDraft: boolean
}) {
  if (running) return <Loader2 aria-label="Atualizando processo" className="size-4 animate-spin text-agent" />
  if (revealing) return <Loader2 aria-label="Atualizando diagrama" className="size-4 animate-spin text-agent" />
  if (failed) return <AlertTriangle aria-label="Processo não estruturado" className="size-4 text-destructive" />
  if (hasDraft) return <Check aria-label="Esboço atualizado" className="size-4 text-success" />
  return <CircleDot aria-label="Esboço iniciado" className="size-4 text-muted-foreground" />
}

function footerIcon(failed: boolean, awaitingReview: boolean, running: boolean) {
  if (failed) return <AlertTriangle aria-hidden="true" className="size-3.5" />
  if (running) return <Loader2 aria-hidden="true" className="size-3.5 animate-spin text-agent" />
  if (awaitingReview) return <Check aria-hidden="true" className="size-3.5 text-success" />
  return <CircleDot aria-hidden="true" className="size-3.5 text-agent" />
}

function footerText(procedure: ProcedurePresentation | null, failed: boolean, awaitingReview: boolean, running: boolean) {
  if (failed) return procedure?.progress?.message || 'Não foi possível estruturar o processo'
  if (awaitingReview) return 'Rascunho pronto para sua revisão'
  if (running) return procedure?.progress?.message || 'Estruturando o processo'
  return procedure?.progress?.message || 'Esboço salvo nesta conversa'
}

function stepIcon(index: number, count: number) {
  if (index === 0) return <Play aria-hidden="true" className="size-3" />
  if (index === count - 1) return <Flag aria-hidden="true" className="size-3" />
  return <CircleDot aria-hidden="true" className="size-3" />
}

function isFailureStatus(status?: string) {
  return ['failed', 'error', 'denied', 'needs_authorization'].includes(status || '')
}
